import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import SegmentMarkers from './SegmentMarkers';
import MediaMarkers from './MediaMarkers';
import TickMarkers from './TickMarkers';
import styles from './VideoTimeline.module.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSearchPlus, faSearchMinus, faCut, faGripLinesVertical, faClock, faBroom, faEllipsisVertical, faLocationCrosshairs } from '@fortawesome/free-solid-svg-icons';
import { Caption, ModalData } from '../../types';
import { MediaItem } from '../../types';
import { fillMediaItemGaps } from '../VideoEditor/fillGapService';
import CropEditToggle from '../VideoEditor/CropEditToggle';
import { isAdminUser } from '../../config/environment';
import { useAuth } from '../../context/Auth/AuthContext';

interface TimelineProps {
  duration: number;
  currentTime: number;
  segments: Caption[];
  onSeek: (time: number) => void;
  setModalData: (modalData: ModalData) => void;
  mediaItems?: MediaItem[];
  setMediaItems?: React.Dispatch<React.SetStateAction<MediaItem[]>>;
  setSegments?: React.Dispatch<React.SetStateAction<Caption[]>>;
  audioUrl: string;
  isLoading?: boolean;
  selectedMediaItemId?: string | null;
  setSelectedMediaItemId?: React.Dispatch<React.SetStateAction<string | null>>;
  addAtCurrentTime?: boolean;
  setAddAtCurrentTime?: React.Dispatch<React.SetStateAction<boolean>>;
  onSplitMediaItem?: (itemId: string, splitTime?: number) => void;
  isVideoMode?: boolean; // true = video-only mode (no audio bar), false = audio+media mode (show audio bar)
  isVideoSegmented?: boolean; // NEW: indicates if video has segments
  onSegmentClick?: (segment: MediaItem) => void; // NEW: handler for segment clicks
  onCleanupClick?: () => void; // NEW: handler for cleanup button
  isCropEditActive?: boolean; // NEW: crop/edit mode state
  onCropEditToggle?: (active: boolean) => void; // NEW: crop/edit toggle handler
  disabledBtnPendingDownload?: boolean; // NEW: disabled state
  project?: {
    projectId: string;
    originalVideoUrl: string;
    duration: string;
    name: string;
  }; // NEW: project identifier for reset
}

const segmentColors = [
  'rgba(255, 99, 132, 0.3)', // pink
  'rgba(54, 162, 235, 0.3)', // blue
  'rgba(255, 206, 86, 0.3)', // yellow
  'rgba(75, 192, 192, 0.3)', // green
  'rgba(153, 102, 255, 0.3)', // purple
];

const VideoTimeline: React.FC<TimelineProps> = ({
  duration,
  currentTime,
  segments,
  onSeek,
  setModalData,
  mediaItems,
  setMediaItems,
  audioUrl,
  setSegments,
  isLoading = false, // NEW: default to false
  selectedMediaItemId, // NEW: selected media item
  setSelectedMediaItemId, // NEW: setter for selected media item
  addAtCurrentTime = false, // NEW: checkbox state
  setAddAtCurrentTime, // NEW: checkbox setter
  onSplitMediaItem, // NEW: split handler
  isVideoMode = false, // NEW: video mode flag
  isVideoSegmented = false, // NEW: segmented video flag
  onSegmentClick, // NEW: segment click handler
  onCleanupClick, // NEW: cleanup click handler
  isCropEditActive = false, // NEW: crop/edit mode state
  onCropEditToggle, // NEW: crop/edit toggle handler
  disabledBtnPendingDownload = false, // NEW: disabled state
  project, // NEW: reset click handler
}) => {
  const { t } = useTranslation('videoEditor');
  const [zoomController, setZoom] = useState(1);
  const [showResetMenu, setShowResetMenu] = useState(false);
  const [autoCenter, setAutoCenter] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const basePixelsPerSecond = 30; // Pixels per second for timeline scaling
  const { user } = useAuth();
  
  // Calculate the actual end time including all media items
  const actualEndTime = mediaItems && mediaItems.length > 0 
    ? Math.max(
        duration,
        ...mediaItems.map(item => item.end)
      )
    : duration;
  
  const audioWidth = actualEndTime * basePixelsPerSecond * zoomController;
  const timelineWidth = Math.max(audioWidth, 1000); // Minimum 1000px for visibility

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setShowResetMenu(false);
      }
    };

    if (showResetMenu) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showResetMenu]);

  // Auto-center timeline to keep playhead in view
  useEffect(() => {
    if (!autoCenter || !scrollContainerRef.current) return;

    const scrollContainer = scrollContainerRef.current;
    const viewportWidth = scrollContainer.clientWidth;
    
    // Calculate playhead position in pixels
    const playheadPositionPx = (currentTime / duration) * timelineWidth;
    
    // Calculate scroll position to center the playhead
    const targetScrollLeft = playheadPositionPx - (viewportWidth / 2);
    
    // Clamp to valid scroll range
    const maxScroll = scrollContainer.scrollWidth - viewportWidth;
    const clampedScroll = Math.max(0, Math.min(targetScrollLeft, maxScroll));
    
    // Smooth scroll to center position
    scrollContainer.scrollTo({
      left: clampedScroll,
      behavior: 'smooth'
    });
  }, [currentTime, autoCenter, duration, timelineWidth]);

  const handleClick = (e: React.MouseEvent<HTMLDivElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const clickedTime = (x / rect.width) * actualEndTime;
    onSeek(clickedTime);
    
    // NEW: Clear selected media item when clicking on empty space
    if (setSelectedMediaItemId) {
      setSelectedMediaItemId(null);
    }
  };

  const handleSegmentClick = (e: React.MouseEvent, segment: Caption) => {
    e.stopPropagation();
    console.log("----segment-> ", segment);
    const modalData: ModalData = {
      segment,
      isOpen: true,
      onClose: () => {},
      targetId: segment.id,
    };
    setModalData(modalData);
  };

  const handleMediaMarkerClick = (e: React.MouseEvent, mediaMarker: MediaItem) => {
    e.stopPropagation();
    console.log("----mediaMarker-> ", mediaMarker);
    
    // NEW: Set the selected media item
    if (setSelectedMediaItemId) {
      setSelectedMediaItemId(mediaMarker.id);
    }
    
    // NEW: If this is a video segment, load it on demand
    if (isVideoSegmented && mediaMarker.type === 'video' && onSegmentClick) {
      console.log(`📦 Loading video segment on click: ${mediaMarker.name}`);
      onSegmentClick(mediaMarker);
    }
    
    const modalData: ModalData = {
      mediaItem: mediaMarker,
      isOpen: true,
      onClose: () => {},
      targetId: mediaMarker.id,
    };
    setModalData(modalData);
  };

  const onResetClick = () => {
    const duration = +project.duration.split(':')[0]*60 + +project.duration.split(':')[1];
    console.log("----project duration-> ", duration);
    
    // Create single media item from original video
    const resetItem: MediaItem = {
      id: `reset-item-${Date.now()}`,
      start: 0,
      end: +duration, // ✅ FIX: Use duration in seconds, not formatted string
      length: +duration, // ✅ FIX: Use duration in seconds, not formatted string
      name: 'item-0',
      type: 'video',
      src: project.originalVideoUrl,
      url: project.originalVideoUrl
    };
    console.log(project);
    //console.log(resetItem);
    setMediaItems([resetItem]); // Replace all segments with original
    //onMediaRested(project.originalVideoUrl, 'Media-reset', '60');
    //onTimelineLoadingChange(false); // ✅ Turn off loading overlay
    console.log("✅ Video reset to original state.");
    setShowResetMenu(false); // Close menu after reset
  };

  const handleMediaMarkersUpdate = (updatedMediaItems: MediaItem[]) => {
    if (setMediaItems) {
      const sortedMediaItems = [...updatedMediaItems].sort((a, b) => a.start - b.start);
      setMediaItems(sortedMediaItems);
    }
  };

  // Handler for Fill Gap button
  const handleFillGaps = () => {
    if (setMediaItems && mediaItems) {
      const filledItems = fillMediaItemGaps(mediaItems);
      setMediaItems(filledItems);
    }
  };

  // Compute tick markers
  const tickInterval = Math.max(1, Math.ceil(actualEndTime / 10));
  const ticks: number[] = [];
  for (let i = 0; i <= actualEndTime; i += tickInterval) {
    ticks.push(i);
  }

  // Check if split is possible for the selected item
  const selectedItem = selectedMediaItemId 
    ? mediaItems?.find(m => m.id === selectedMediaItemId) 
    : null;
  const canSplit = selectedItem 
    ? (currentTime > selectedItem.start && currentTime < selectedItem.end)
    : false;

  return (
    <>
      {/* Combined Controls - Zoom, Split, and Checkbox */}
      <div className={styles.controlsContainer}>
        {/* Zoom Controls */}
        <div className={styles.zoomControls}>
          <button
            onClick={() => setZoom(prev => prev * 1.2)}
            className={styles.zoomButton}
          >
            <FontAwesomeIcon icon={faSearchPlus} className={styles.zoomIcon} />
          </button>
          <button
            onClick={() => setZoom(prev => prev / 1.2)}
            className={styles.zoomButton}
          >
            <FontAwesomeIcon icon={faSearchMinus} className={styles.zoomIcon} />
          </button>
        </div>

        {/* Auto-Center Button */}
        <button
          onClick={() => setAutoCenter(!autoCenter)}
          className={`${styles.autoCenterButton} ${autoCenter ? styles.autoCenterActive : ''}`}
          title="Auto-center timeline on playhead"
        >
          <FontAwesomeIcon icon={faLocationCrosshairs} className={styles.autoCenterIcon} />
        </button>

        {/* Split Button - Always show but disabled when no selection */}
        {onSplitMediaItem && (
          <button
            onClick={() => {
              if (selectedItem && canSplit) {
                onSplitMediaItem(selectedMediaItemId!, currentTime);
              }
            }}
            className={styles.splitButton}
            disabled={!selectedMediaItemId}
            style={{
              opacity: !selectedMediaItemId ? 0.4 : !canSplit ? 0.7 : 1,
              cursor: !selectedMediaItemId ? 'not-allowed' : !canSplit ? 'not-allowed' : 'pointer'
            }}
            title={
              !selectedMediaItemId
                ? "Select a media item from the timeline first"
                : !canSplit 
                ? "Move playhead inside the selected media item to split"
                : "Split media at current location"
            }
          >
            <FontAwesomeIcon icon={faCut} className={styles.splitIcon} />
          </button>
        )}

        {/* Fill Gap Button - Show when there are media items */}
        {mediaItems && mediaItems.length > 1 && setMediaItems && (
          <button
            onClick={handleFillGaps}
            className={styles.fillGapButton}
            title="Fill gaps between media items"
          >
            <FontAwesomeIcon icon={faGripLinesVertical} className={styles.fillGapIcon} />
          </button>
        )}

        {/* Crop/Edit Toggle Button - Always show but disabled when no selection */}
        {mediaItems && mediaItems.length > 0 && onCropEditToggle && (
          <CropEditToggle
            isActive={isCropEditActive}
            onToggle={onCropEditToggle}
            disabled={disabledBtnPendingDownload}
            hasMediaItems={mediaItems.length > 0}
            hasSelectedMedia={!!selectedMediaItemId}
          />
        )}

        {/* Add at Current Time Toggle Button */}
        {setAddAtCurrentTime && (
          <button
            onClick={() => {
              if (setAddAtCurrentTime) {
                setAddAtCurrentTime(!addAtCurrentTime);
              }
            }}
            className={`${styles.timeToggleButton} ${addAtCurrentTime ? styles.timeToggleActive : ''}`}
            title="Add new media at current time position"
          >
            <FontAwesomeIcon icon={faClock} className={styles.timeToggleIcon} />
          </button>
        )}

        {/* AI Cleanup Button - Show when there are media items */}
        { isAdminUser(user) &&  mediaItems && mediaItems.length > 0 && onCleanupClick && (
          <button
            onClick={onCleanupClick}
            className={styles.cleanupButton}
            title="AI Cleanup - Remove filler words and repetitions"
          >
            <FontAwesomeIcon icon={faBroom} className={styles.cleanupIcon} />
          </button>
        )}

        {/* Reset Button - Only show in video mode when segments exist */}
        {isVideoMode && !audioUrl && mediaItems && mediaItems.length > 0  && (
          <div style={{ position: 'relative' }} ref={menuRef}>
            <button
              onClick={() => setShowResetMenu(!showResetMenu)}
              className={styles.menuButton}
              title="More options"
            >
              <FontAwesomeIcon icon={faEllipsisVertical} className={styles.menuIcon} />
            </button>
            
            {showResetMenu && (
              <div className={styles.resetMenu}>
                <button
                  onClick={onResetClick}
                  className={styles.resetMenuItem}
                >
                 {t('videoEditor.timeline.reset')}
                </button>
              </div>
            )}
          </div>
        )}
      </div>


      {/* Main Timeline */}
      <div 
        ref={scrollContainerRef}
        style={{ 
          overflowX: 'auto', 
          overflowY: 'hidden',
          border: '1px solid #444', 
          position: 'relative', 
          minWidth: '100%', 
          height: '230px' 
        }}
      >
        {/* Loading Overlay - Position relative to parent container */}
        {isLoading && (
          <div className={styles.loadingOverlay}>
            <div className={styles.loadingSpinner}></div>
            <div className={styles.loadingText}>{t('videoEditor.timeline.loading')}</div>
          </div>
        )}
        
        <div
          className={styles.timelineContainer}
          onClick={handleClick}
          style={{ width: `${timelineWidth}px`, height: '230px' }} // Use exact pixel width and fixed height for proper scaling
        >
          <div
            className={styles.progress}
            style={{ width: `${(currentTime / actualEndTime) * 100}%` }}
          />
          
          {/* Audio Timeline Bar - Only shown in AUDIO+MEDIA mode */}
          {/* In video-only mode (isVideoMode=true), no audio bar is shown */}
          {audioUrl && !isVideoMode && (
            <div
              style={{
                position: 'absolute',
                left: 0,
                bottom: 0,
                width: '100%',
                height: '60px',
                backgroundColor: '#10b981', // Green for audio mode
                borderRadius: '4px',
                zIndex: 1
              }}
            >
              {/* Waveform visualization */}
              <div className={styles.audioWaveform}>
                {(() => {
                  const barCount = Math.min(Math.floor(duration * 2), 200);
                  
                  return Array.from({ length: barCount }, (_, i) => {
                    const leftPercent = (i / barCount) * 100;
                    const widthPercent = (100 / barCount) * 0.8;
                    return (
                      <div
                        key={`timeline-waveform-${i}`}
                        className={styles.waveformBar}
                        style={{ 
                          height: `${Math.random() * 70 + 30}%`,
                          width: `${widthPercent}%`,
                          position: 'absolute',
                          left: `${leftPercent}%`,
                          bottom: '2px'
                        }}
                      />
                    );
                  });
                })()}
              </div>
              
              {/* Progress indicator */}
              <div 
                className={styles.audioTimelineProgress} 
                style={{ 
                  width: `${(currentTime / duration) * 100}%`,
                  height: '100%' 
                }} 
              />
            </div>
          )}
          
          <SegmentMarkers
            segments={segments}
            duration={actualEndTime}
            segmentColors={segmentColors}
            onSegmentClick={handleSegmentClick}
            setSegments = {setSegments!}
          />
          {mediaItems && setMediaItems && mediaItems.length > 0 && (
            <MediaMarkers
              mediaItems={mediaItems}
              duration={actualEndTime}
              segmentColors={segmentColors}
              onMediaMarkerClick={handleMediaMarkerClick}
              onMediaMarkersUpdate={handleMediaMarkersUpdate}
              setMediaItems={setMediaItems}
              selectedMediaItemId={selectedMediaItemId}
              setSelectedMediaItemId={setSelectedMediaItemId}
            />
          )}
          <TickMarkers ticks={ticks} duration={actualEndTime} />
        </div>
      </div>
    </>
  );
};

export default VideoTimeline;
